import React from "react";
import { useFormikContext } from "formik";
import { Box, ClickAwayListener, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import * as yup from "yup";
import { customNumberFormat, RoadsideTextField } from "components/FormElements/FormElements.styled";
import { InferType } from "yup";
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons/faInfoCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NumberFormattingType } from "CommonTypes";

export const schemaShape = {
    contactForm: yup.object({
        emailAddress: yup
            .string()
            .required("Please enter your email address.")
            .email("Please enter a valid email address."),
        preferredPhone: yup
            .string()
            .required("Please enter your preferred phone number.")
            .matches(
                /^(02|03|04|05|07|08)[0-9]{8}$/,
                "Please enter a 10 digit phone number (include an area code for landline numbers).",
            ),
    }),
};

const schema = yup.object().shape(schemaShape);

type Values = InferType<typeof schema>;

export const ContactForm: React.FC = () => {
    const theme = useTheme();
    const { values, errors, touched, handleChange, handleBlur } = useFormikContext<Values>();
    const [open, setOpen] = React.useState(false);

    const handleTooltipClose = () => {
        setOpen(false);
    };

    const handleTooltipOpen = () => {
        setOpen(!open);
    };

    return (
        <Box sx={{ width: "100%", maxWidth: "475px" }}>
            <Box marginBottom={3}>
                <Typography variant="subtitle1">
                    Your product documents and other product related communications, including renewal information, will
                    be sent to the contact details below.
                </Typography>
            </Box>

            <RoadsideTextField
                variant="outlined"
                fullWidth
                name="contactForm.emailAddress"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.contactForm.emailAddress}
                label="Email address"
                error={touched.contactForm?.emailAddress && Boolean(errors.contactForm?.emailAddress)}
                helperText={touched.contactForm?.emailAddress && errors.contactForm?.emailAddress}
            />
            <Stack direction={"row"} sx={{ position: "relative", marginBottom: 3 }} alignItems={"flex-start"}>
                <RoadsideTextField
                    variant="outlined"
                    fullWidth
                    name="contactForm.preferredPhone"
                    inputProps={{
                        type: NumberFormattingType.phoneNumber,
                    }}
                    InputProps={{
                        inputComponent: customNumberFormat as any,
                    }}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.contactForm.preferredPhone}
                    label="Preferred phone"
                    error={touched.contactForm?.preferredPhone && Boolean(errors.contactForm?.preferredPhone)}
                    helperText={touched.contactForm?.preferredPhone && errors.contactForm?.preferredPhone}
                    sx={{ marginBottom: 0 }}
                />

                <ClickAwayListener onClickAway={handleTooltipClose}>
                    <div>
                        <Tooltip
                            PopperProps={{
                                disablePortal: true,
                            }}
                            onClose={handleTooltipClose}
                            open={open}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                            title="Please enter a mobile phone number so we can communicate effectively with you, however, a landline can also be entered."
                            placement="top-end"
                        >
                            <Box
                                onClick={handleTooltipOpen}
                                sx={{
                                    cursor: "pointer",
                                    marginTop: 2.1,
                                    position: "absolute",
                                    zIndex: 1,
                                    paddingLeft: "10px",
                                    display: { xs: "none", sm: "block" },
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faInfoCircle}
                                    style={{
                                        width: 24,
                                        height: 24,
                                        color: open ? theme.palette.primary.main : theme.palette.offGrey,
                                    }}
                                />
                            </Box>
                        </Tooltip>
                    </div>
                </ClickAwayListener>
            </Stack>
        </Box>
    );
};
